import React, { lazy, Suspense, useState, useEffect } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Login from "./Auth/Login";
import AuthServieApi from "./api/AuthApi";
import { message } from "antd";
import UserApi from "./api/UserApi";
import { initGA, trackPageView } from "./utils/analytics";
import StoreApi from "./api/StoreApi";
import ItemsListType from "./api/Items List/itemListApi";
import { GenericGlassTypes } from "./utils/constants";

const MainPage = lazy(() => import("./components/SideNav"));
const SignUp = lazy(() => import("./Auth/SignUp"));
const ResetPassword = lazy(() => import("./Auth/Reset"));
const SetNewPassword = lazy(() => import("./Auth/SetPassword"));
const UserInfo = lazy(() => import("./Auth/SignUp/userInfo"));
const StoreInfo = lazy(() => import("./Auth/SignUp/storeInfo"));
const MobileUploadPage = lazy(
  () => import("./components/Mobile View/Customers/mobile-upload")
);
const EstorePublicShop = lazy(
  () => import("./components/PublicShop/EstorePublicShop")
);

// Component to track page views
const Analytics = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [members, setMembers] = useState([]);
  const [stores, setStores] = useState([]);
  const [user, setUser] = useState([]);
  const [memberData, setMemberData] = useState([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  // Initialize GA4
  useEffect(() => {
    initGA();
  }, []);

  
  // PWA Installation prompt handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show the install button
      setShowInstallButton(true);
    };

    const handleAppInstalled = () => {
      // Hide the install button when the app is installed
      setShowInstallButton(false);
      // Clear the deferred prompt
      setDeferredPrompt(null);
    };

    // Listen for the beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    // Listen for the appinstalled event
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Function to show the installation prompt
  const handleInstallClick = () => {
    if (!deferredPrompt) return;
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt, then clear it
    deferredPrompt.userChoice.then(() => {
      setDeferredPrompt(null);
      setShowInstallButton(false);
    });
  };

  const navigate = useNavigate();

  const handleSignUp = async () => {
    // const storeDescription = "Default store description";
    const { data, error } = await AuthServieApi.signUp(
      email,
      password
      // storeDescription
    );

    if (error) {
      message.error("This Email is already registered. Please Log in instead.");
      return { data, success: false };
    } else {
      // After successful signup, we should update the user and memberData states
      // with the new user's data
      if (data && data.user) {
        setUser(data.user);
        if (data.member) {
          setMemberData(data.member);
        }
      }
      return { data, success: true };
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) {
      alert(`Please wait ${resendCooldown} seconds before resending.`);
      return;
    }

    const { data, error } = await AuthServieApi.resendConfirmationEmail(email);

    if (error) {

      if (error.code === "over_email_send_rate_limit") {
        setResendCooldown(60);
      }

      return { data, success: false };
    } else {

      setResendCooldown(60);

      return { data, success: true };
    }
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleUpdateMember = async (updatedMember) => {
    try {
      // Ensure API call returns a response before destructuring
      const response = await AuthServieApi.updateMember(updatedMember);

      if (!response) {
        throw new Error("No response from API");
      }

      const { data, error } = response;

      if (error) {
        console.error("Error updating member:", error.message);
        return { data: null, success: false };
      }

      // Ensure members exist before updating
      setMembers(
        (prevMembers) =>
          prevMembers?.map((member) =>
            member.id === updatedMember.id
              ? { ...member, ...data.member }
              : member
          ) || []
      );

      return { data, success: true };
    } catch (err) {
      console.error("Unexpected error in handleUpdateMember:", err);
      return { data: null, success: false };
    }
  };

  // const handleUpdateStore = async (updatedStore) => {
  //   await AuthServieApi.updateStore(updatedStore);
  //   const updatedStores = stores.map((store) =>
  //     store.id === updatedStore.id ? updatedStore : store
  //   );
  //   setStores(updatedStores);
  // };

  const handleUpdateStore = async (updatedStore) => {
    try {
      // Ensure API call returns a response before destructuring
      const response = await AuthServieApi.updateStore(updatedStore);

      if (!response) {
        throw new Error("No response from API");
      }

      const { data, error } = response;

      if (error) {
        console.error("Error updating store:", error.message);
        return { data: null, success: false };
      }

      
      const storeId = data?.[0]?.s_id
      const storeType = data?.[0]?.type
      
      if (!storeId) {
        console.error("Store ID not found in response data");
        return { data: null, success: false };
      }

      if (storeType === "Wholesale") {
        // Add all generic glass types as item list types
        const addGenericItemsPromises = GenericGlassTypes.map(async (item) => {
          const itemPayload = {
            name: item.value,
            category: "Glasses",
            s_id: storeId
          };
          return await ItemsListType.addGenericItemListType(itemPayload);
        });

        // Wait for all generic items to be added
        const results = await Promise.all(addGenericItemsPromises);
      }

      // Ensure stores exist before updating
      setStores(
        (prevStores) =>
          prevStores?.map((store) =>
            store.id === updatedStore.id ? { ...store, ...data.store } : store
          ) || []
      );

      return { data, success: true };
    } catch (err) {
      console.error("Unexpected error in handleUpdateStore:", err);
      return { data: null, success: false };
    }
  };

  const handleSignIn = async () => {
    const { data, error } = await AuthServieApi.signIn(email, password);
    
    if (error) {
      if (error.message.toLowerCase().includes("email not confirmed")) {
        message.error(
          "Your email is not confirmed. Please verify it before logging in."
        );
      } else {
        message.error(`${error.message}`);
      }
    } else {
      message.success("Logged in successfully!");
      setIsAuthenticated(true);
      
      // Check if user has stores and redirect appropriately
      try {
        const authUser = await UserApi.fetchUser();
        if (authUser) {
          const userStore = await StoreApi.fetchStore(authUser.id);
          if (userStore && userStore.store) {
            const storeList = Array.isArray(userStore.store) ? userStore.store : [userStore.store];
            // If there's only one store, redirect to that store directly
            if (storeList.length === 1) {
              navigate(`/store/${storeList[0].s_id}/dashboard`);
              return;
            }
          }
        }
      } catch (error) {
        console.error("Error checking stores after login:", error);
      }
      
      // If multiple stores or error checking, go to memberstores
      navigate("/memberstores");
    }
  };

  const handleSignOut = async () => {
    const error = await AuthServieApi.signOut();
    if (error) {
      console.error("Sign out error:", error.message || error);
      return;
    }
    setIsAuthenticated(false);
    // Clear user and member data states to prevent conflicts with new users
    setUser([]);
    setMemberData([]);
    navigate("/login");
  };

  // useEffect(() => {
  //   // Listen for new order insertions
  //   const orderSubscription = BaseApi.supabase
  //     .channel("orders")
  //     .on(
  //       "postgres_changes",
  //       { event: "INSERT", schema: "public", table: "orders" },
  //       (payload) => {
  //         toast.success(`New Order Received: #${payload.order_id}`, {
  //           position: "top-right",
  //           autoClose: 5000,
  //         });
  //       }
  //     )
  //     .subscribe();

  //   return () => {
  //     BaseApi.supabase.removeChannel(orderSubscription);
  //   };
  // }, []);

  return (
    // <Router>
    <>
      <Analytics />
      {/* <NotificationListener ></NotificationListener> */}
      {/* <ToastContainer></ToastContainer> */}

      <Suspense
        fallback={
          <div style={{ textAlign: "center", padding: "50px" }}>
            Loading...
          </div>
        }
      >
        <Routes>
          <Route
            path="/login"
            element={
              <Login
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                onLogin={handleSignIn}
              />
            }
          />

        {/* <Route
          path="/memberstores"
          element={<MemberStores></MemberStores>}
        ></Route> */}

          <Route
            path="/signup"
            element={
              <SignUp
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                onSignUp={handleSignUp}
                onResendEmail={handleResendEmail}
                resendCooldown={resendCooldown}
              />
            }
          />
          <Route
            path="/userInfo/:memberId"
            // path="/userInfo/" // for email Confirmation
            element={
              <UserInfo
                userId={user.id}
                memberId={memberData?.id}
                updateMember={handleUpdateMember}
              />
            }
          />
          <Route
            path="/storeInfo/:userId"
            element={<StoreInfo updateStore={handleUpdateStore} />}
          />

          <Route path="/forgetpassword" element={<ResetPassword />} />
          <Route path="/SetNewPassword" element={<SetNewPassword />} />

          {/* Mobile Upload Route */}
          <Route path="/mobile-upload" element={<MobileUploadPage />} />

          <Route path="/shop/:slug" element={<EstorePublicShop />} />

          <Route
            path="/*"
            element={
              // <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MainPage
                authUser={user}
                password={password}
                setPassword={setPassword}
                onLogout={handleSignOut}
                showInstallButton={showInstallButton}
                handleInstallClick={handleInstallClick}
              />
              // </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>

    </>
    // </Router>
  );
}

export default App;